import { useEffect, useState, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Grid,
  IconButton,
  Autocomplete,
  Select,
  MenuItem,
  Chip,
  Divider,
  alpha,
  useTheme,
  CircularProgress,
  Switch,
  FormControlLabel,
  Tooltip,
} from "@mui/material";
import {
  ArrowLeft,
  Save,
  Trash2,
  Users,
  Building2,
  TrendingUp,
  Ban,
  CheckCircle2,
  Rocket,
} from "lucide-react";
import {
  fetchMarketingOrganization,
  createMarketingOrganization,
  updateMarketingOrganization,
  linkContactToMarketingOrg,
  unlinkContactFromMarketingOrg,
  fetchOrgTasks,
  fetchOrgActivity,
} from "../../../../../../redux/actions/marketingAction";
import { enqueueSnackbar } from "notistack";
import { backendClient } from "../../../../../../api/backend";
import FicheEmail from "./OrganizationFiche/FicheEmail";
import FicheQuotes from "./OrganizationFiche/FicheQuotes";
import FicheTasks from "./OrganizationFiche/FicheTasks";
import FicheActivity from "./OrganizationFiche/FicheActivity";
import OnboardOrgDialog from "./OnboardOrgDialog";

const STATUS_OPTIONS = [
  { value: "lead", label: "Lead", color: "#6b7280" },
  { value: "testing", label: "Testing", color: "#3b82f6" },
  { value: "demo_booked", label: "Demo Booked", color: "#8b5cf6" },
  { value: "proposal", label: "Proposal", color: "#f59e0b" },
  { value: "closed", label: "Closed", color: "#10b981" },
  { value: "lost", label: "Lost", color: "#ef4444" },
];

const SIZE_OPTIONS = ["1-10", "11-50", "51-200", "200+"];
const SOURCE_OPTIONS = ["linkedin", "website", "referral", "cold_outreach", "event", "other"];
const CURRENCY_OPTIONS = ["MAD", "USD", "EUR"];

const MarketingOrgDetail = ({ orgId }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const { currentOrganization, orgTasks, orgActivity } = useSelector((state) => state.marketing);
  const isEdit = Boolean(orgId);

  const [form, setForm] = useState({
    name: "", ice: "", address: "", city: "", country: "Maroc", phone: "", website: "", notes: "",
    status: "lead", estimated_value: "", currency: "MAD", industry: "", size: "", source: "",
    is_active: true,
  });
  const [saving, setSaving] = useState(false);
  const [allContacts, setAllContacts] = useState([]);
  const [linkingContact, setLinkingContact] = useState(null);
  const [onboardOpen, setOnboardOpen] = useState(false);

  useEffect(() => {
    if (isEdit) {
      dispatch(fetchMarketingOrganization(orgId));
      dispatch(fetchOrgTasks(orgId));
      dispatch(fetchOrgActivity(orgId));
    }
    backendClient.getMarketingContacts?.({ page_size: 100 })
      ?.then?.((data) => setAllContacts(data.items || []))
      ?.catch?.(() => {});
  }, [dispatch, orgId, isEdit]);

  // Initialize form once per org — do NOT reset when currentOrganization
  // gets reloaded from side effects (link/unlink contact, etc.) since that
  // would clobber the user's in-progress edits.
  const initializedOrgRef = useRef(null);
  useEffect(() => {
    if (
      isEdit &&
      currentOrganization &&
      currentOrganization.id === orgId &&
      initializedOrgRef.current !== orgId
    ) {
      setForm({
        name: currentOrganization.name || "",
        ice: currentOrganization.ice || "",
        address: currentOrganization.address || "",
        city: currentOrganization.city || "",
        country: currentOrganization.country || "Maroc",
        phone: currentOrganization.phone || "",
        website: currentOrganization.website || "",
        notes: currentOrganization.notes || "",
        status: currentOrganization.status || "lead",
        estimated_value: currentOrganization.estimated_value || "",
        currency: currentOrganization.currency || "MAD",
        industry: currentOrganization.industry || "",
        size: currentOrganization.size || "",
        source: currentOrganization.source || "",
        is_active: currentOrganization.is_active !== false,
      });
      initializedOrgRef.current = orgId;
    }
  }, [isEdit, currentOrganization, orgId]);

  // Keep is_active synced when reloaded after a quick-toggle (form ref guards
  // other fields from being clobbered, but is_active is a one-click action so we
  // want it to reflect the persisted value immediately).
  useEffect(() => {
    if (
      isEdit &&
      currentOrganization &&
      currentOrganization.id === orgId &&
      initializedOrgRef.current === orgId
    ) {
      const persisted = currentOrganization.is_active !== false;
      setForm((prev) => (prev.is_active === persisted ? prev : { ...prev, is_active: persisted }));
    }
  }, [isEdit, currentOrganization, orgId]);

  const handleActiveToggle = async (e) => {
    const next = e.target.checked;
    // Optimistic update
    setForm((prev) => ({ ...prev, is_active: next }));
    try {
      await dispatch(updateMarketingOrganization(orgId, { is_active: next }));
      enqueueSnackbar(
        next ? t("mkt_org_activated_msg") : t("mkt_org_deactivated_msg"),
        { variant: next ? "success" : "warning" }
      );
    } catch (err) {
      // Revert on failure
      setForm((prev) => ({ ...prev, is_active: !next }));
      enqueueSnackbar(t("mkt_activation_update_error"), { variant: "error" });
    }
  };

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      enqueueSnackbar(t("mkt_org_name_required"), { variant: "warning" });
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        estimated_value: form.estimated_value ? parseFloat(form.estimated_value) : null,
      };
      if (isEdit) {
        await dispatch(updateMarketingOrganization(orgId, payload));
        enqueueSnackbar(t("mkt_org_updated"), { variant: "success" });
      } else {
        await dispatch(createMarketingOrganization(payload));
        enqueueSnackbar(t("mkt_org_created"), { variant: "success" });
        navigate("/admin/marketing/organisations");
      }
    } catch (err) {
      enqueueSnackbar(err.message || "Erreur", { variant: "error" });
    } finally {
      setSaving(false);
    }
  };

  const handleLinkContact = async (_, contact) => {
    if (!contact || !isEdit) return;
    try {
      await dispatch(linkContactToMarketingOrg(orgId, contact.id));
      enqueueSnackbar(t("mkt_contact_linked"), { variant: "success" });
      setLinkingContact(null);
    } catch (err) {
      enqueueSnackbar(err.message || "Erreur", { variant: "error" });
    }
  };

  const handleUnlinkContact = async (contactId) => {
    try {
      await dispatch(unlinkContactFromMarketingOrg(orgId, contactId));
      enqueueSnackbar(t("mkt_contact_unlinked"), { variant: "success" });
    } catch (err) {
      enqueueSnackbar(err.message || "Erreur", { variant: "error" });
    }
  };

  const orgContacts = currentOrganization?.contacts || [];
  const linkedContactIds = new Set(orgContacts.map((c) => c.id));
  const availableContacts = allContacts.filter((c) => !linkedContactIds.has(c.id));
  const statusConfig = STATUS_OPTIONS.find((s) => s.value === form.status) || STATUS_OPTIONS[0];

  return (
    <Box sx={{ opacity: isEdit && !form.is_active ? 0.7 : 1, transition: "opacity 0.2s" }}>
      {/* Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <IconButton size="small" onClick={() => navigate("/admin/marketing/organisations")}>
            <ArrowLeft size={18} />
          </IconButton>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {isEdit ? currentOrganization?.name || t("mkt_organisation") : t("mkt_new_organisation")}
          </Typography>
          {isEdit && currentOrganization?.score > 0 && (
            <Chip icon={<TrendingUp size={12} />} label={currentOrganization.score} size="small"
              sx={{ fontWeight: 700, fontSize: "0.7rem", height: 24, bgcolor: alpha("#10b981", 0.1), color: "#10b981", "& .MuiChip-icon": { color: "#10b981" } }}
            />
          )}
          {isEdit && !form.is_active && (
            <Chip
              icon={<Ban size={12} />}
              label={t("mkt_deactivated")}
              size="small"
              sx={{
                fontWeight: 700, fontSize: "0.7rem", height: 24,
                bgcolor: alpha("#ef4444", 0.1), color: "#ef4444",
                "& .MuiChip-icon": { color: "#ef4444" },
              }}
            />
          )}
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          {isEdit && (
            <Tooltip
              title={form.is_active ? t("mkt_org_active_tooltip") : t("mkt_org_inactive_tooltip")}
            >
              <FormControlLabel
                control={
                  <Switch
                    checked={form.is_active}
                    onChange={handleActiveToggle}
                    size="small"
                    color="success"
                  />
                }
                label={
                  <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: form.is_active ? "#10b981" : theme.palette.text.disabled }}>
                    {form.is_active ? t("mkt_active") : t("mkt_inactive")}
                  </Typography>
                }
                labelPlacement="start"
                sx={{ ml: 0, mr: 0, gap: 0.75 }}
              />
            </Tooltip>
          )}
          {isEdit && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<Rocket size={14} />}
              onClick={() => setOnboardOpen(true)}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                borderRadius: "8px",
              }}
            >
              Onboarder
            </Button>
          )}
          <Button
            size="small" variant="contained"
            startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <Save size={14} />}
            onClick={handleSave} disabled={saving}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            {isEdit ? t("common_update") : t("common_create")}
          </Button>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Left — Organization info */}
        <Grid item xs={12} md={7}>
          <Paper elevation={0} sx={{ p: 3, border: `1px solid ${theme.palette.divider}`, borderRadius: "12px", mb: 3 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, textTransform: "uppercase", letterSpacing: 1, fontSize: "0.7rem", color: theme.palette.text.secondary }}>
              {t("mkt_org_info")}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" label={`${t("common_name")} *`} value={form.name} onChange={handleChange("name")} />
              </Grid>
              <Grid item xs={6} sm={3}>
                <Select fullWidth size="small" value={form.status} onChange={handleChange("status")}
                  sx={{ bgcolor: alpha(statusConfig.color, 0.05), "& .MuiOutlinedInput-notchedOutline": { borderColor: alpha(statusConfig.color, 0.2) } }}
                >
                  {STATUS_OPTIONS.map((s) => (
                    <MenuItem key={s.value} value={s.value}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: s.color }} />
                        {s.label}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField fullWidth size="small" label="ICE" value={form.ice} onChange={handleChange("ice")} />
              </Grid>
              <Grid item xs={8}>
                <TextField fullWidth size="small" label={t("mkt_address")} value={form.address} onChange={handleChange("address")} />
              </Grid>
              <Grid item xs={4}>
                <TextField fullWidth size="small" label={t("mkt_city")} value={form.city} onChange={handleChange("city")} />
              </Grid>
              <Grid item xs={4}>
                <TextField fullWidth size="small" label={t("mkt_country")} value={form.country} onChange={handleChange("country")} />
              </Grid>
              <Grid item xs={4}>
                <TextField fullWidth size="small" label={t("mkt_phone")} value={form.phone} onChange={handleChange("phone")} />
              </Grid>
              <Grid item xs={4}>
                <TextField fullWidth size="small" label={t("mkt_website")} value={form.website} onChange={handleChange("website")} />
              </Grid>
              <Grid item xs={4}>
                <TextField fullWidth size="small" label="Industrie" value={form.industry} onChange={handleChange("industry")} />
              </Grid>
              <Grid item xs={4}>
                <Select fullWidth size="small" value={form.size} onChange={handleChange("size")} displayEmpty
                  renderValue={(v) => v || <Typography sx={{ color: theme.palette.text.disabled, fontSize: "0.85rem" }}>Taille...</Typography>}
                >
                  <MenuItem value="">—</MenuItem>
                  {SIZE_OPTIONS.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                </Select>
              </Grid>
              <Grid item xs={4}>
                <Select fullWidth size="small" value={form.source} onChange={handleChange("source")} displayEmpty
                  renderValue={(v) => v || <Typography sx={{ color: theme.palette.text.disabled, fontSize: "0.85rem" }}>Source...</Typography>}
                >
                  <MenuItem value="">—</MenuItem>
                  {SOURCE_OPTIONS.map((s) => <MenuItem key={s} value={s} sx={{ textTransform: "capitalize" }}>{s.replace("_", " ")}</MenuItem>)}
                </Select>
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth size="small" label="Valeur estimée" type="number" value={form.estimated_value} onChange={handleChange("estimated_value")} />
              </Grid>
              <Grid item xs={6}>
                <Select fullWidth size="small" value={form.currency} onChange={handleChange("currency")}>
                  {CURRENCY_OPTIONS.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                </Select>
              </Grid>
            </Grid>
          </Paper>

          {/* Notes */}
          <Paper elevation={0} sx={{ p: 3, border: `1px solid ${theme.palette.divider}`, borderRadius: "12px", mb: 3 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, textTransform: "uppercase", letterSpacing: 1, fontSize: "0.7rem", color: theme.palette.text.secondary }}>
              Notes
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={6}
              value={form.notes}
              onChange={handleChange("notes")}
              placeholder="Notes internes..."
              size="small"
            />
          </Paper>

          {/* Email */}
          {isEdit && currentOrganization && (
            <Paper elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: "12px", mb: 3 }}>
              <FicheEmail org={currentOrganization} orgId={orgId} />
            </Paper>
          )}
        </Grid>

        {/* Right — Contacts + Tasks + Activity */}
        <Grid item xs={12} md={5}>
          <Paper elevation={0} sx={{ p: 3, border: `1px solid ${theme.palette.divider}`, borderRadius: "12px", mb: 3 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, textTransform: "uppercase", letterSpacing: 1, fontSize: "0.7rem", color: theme.palette.text.secondary }}>
              {t("mkt_contacts")} ({orgContacts.length})
            </Typography>
            {isEdit ? (
              <>
                <Autocomplete
                  options={availableContacts}
                  getOptionLabel={(c) => `${c.first_name || ""} ${c.last_name || ""} — ${c.email}`}
                  value={linkingContact}
                  onChange={handleLinkContact}
                  renderInput={(params) => <TextField {...params} placeholder={t("mkt_link_contact")} size="small" />}
                  size="small" sx={{ mb: 2 }}
                />
                {orgContacts.length === 0 ? (
                  <Box sx={{ textAlign: "center", py: 3 }}>
                    <Users size={32} strokeWidth={1} color={theme.palette.text.disabled} />
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>{t("mkt_no_linked_contacts")}</Typography>
                  </Box>
                ) : (
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    {orgContacts.map((contact) => (
                      <Box key={contact.id} sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", p: 1.5, borderRadius: "8px", border: `1px solid ${theme.palette.divider}` }}>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {[contact.first_name, contact.last_name].filter(Boolean).join(" ") || "—"}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">{contact.email}</Typography>
                          {contact.phone && <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>{contact.phone}</Typography>}
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          {contact.lead_score > 0 && (
                            <Chip icon={<TrendingUp size={10} />} label={contact.lead_score} size="small"
                              sx={{ height: 20, fontSize: "0.6rem", fontWeight: 700, bgcolor: alpha("#10b981", 0.1), color: "#10b981", "& .MuiChip-icon": { color: "#10b981" } }}
                            />
                          )}
                          <IconButton size="small" onClick={() => handleUnlinkContact(contact.id)}><Trash2 size={14} /></IconButton>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                )}
              </>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", py: 3 }}>{t("mkt_create_org_first")}</Typography>
            )}
          </Paper>

          {/* Quotes */}
          {isEdit && (
            <Paper elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: "12px", mb: 3 }}>
              <FicheQuotes orgId={orgId} org={currentOrganization} />
            </Paper>
          )}

          {/* Tasks */}
          {isEdit && (
            <Paper elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: "12px", mb: 3 }}>
              <FicheTasks orgId={orgId} tasks={orgTasks || []} />
            </Paper>
          )}

          {/* Activity */}
          {isEdit && (
            <Paper elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: "12px" }}>
              <FicheActivity orgId={orgId} activity={orgActivity} />
            </Paper>
          )}
        </Grid>
      </Grid>

      <OnboardOrgDialog
        open={onboardOpen}
        onClose={() => setOnboardOpen(false)}
        org={currentOrganization}
      />
    </Box>
  );
};

export default MarketingOrgDetail;
